import { useState, useEffect } from "react";

interface EmailValidationResult {
  valid: boolean | null;
  message?: string;
  isValidating: boolean;
}

export function useEmailValidation(
  email: string,
  delay = 1000,
): EmailValidationResult {
  const [validation, setValidation] = useState<EmailValidationResult>({
    valid: null,
    message: undefined,
    isValidating: false,
  });

  useEffect(() => {
    // Reset validation if email is empty or doesn't have @
    if (!email || !email.includes("@")) {
      setValidation({ valid: null, message: undefined, isValidating: false });
      return;
    }

    // Basic format check first
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      setValidation({
        valid: false,
        message: "✗ Formato de email inválido",
        isValidating: false,
      });
      return;
    }

    setValidation({ valid: null, message: undefined, isValidating: true });

    const validateEmail = async () => {
      try {
        const response = await fetch(
          `https://api.likn.dev/v1/public/email-validation/validate?email=${encodeURIComponent(email)}`,
        );
        const data = await response.json();

        if (data.valid) {
          setValidation({
            valid: true,
            message: `✓ Email válido${data.provider ? ` (${data.provider})` : ""}`,
            isValidating: false,
          });
        } else {
          const errors = data.errors?.join(", ") || "Email inválido";
          setValidation({
            valid: false,
            message: `✗ ${errors}`,
            isValidating: false,
          });
        }
      } catch (error) {
        console.error("Erro ao validar email:", error);
        // Don't set error state, just stop validating
        setValidation({ valid: null, message: undefined, isValidating: false });
      }
    };

    const timeoutId = setTimeout(validateEmail, delay);
    return () => clearTimeout(timeoutId);
  }, [email, delay]);

  return validation;
}
